import React, { useEffect, useState } from 'react';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EntityScreeningResultsTable from 'components/Screening/tables/entityTable/EntityScreeningResultsTable';
import { normalizeExternalUrl } from 'utils/common';

interface IEntityTicketResultsProps {
  // Entity AI evaluation saved with the ticket (same shape produced by the
  // Entity Screening tool's risk evaluation).
  aiSums: any[];
  // Beneficial Ownership AI evaluation saved with the ticket (null if none).
  beneficialOwnership?: any;
  // Adverse Media result saved with the ticket (null if none).
  adverseMedia?: any;
  // Reconstructed screening results in the EntityScreeningResultsTable shape:
  // { isLoading, data: [{ result: { body: { hits: { hits, total } } } }] }.
  results: any;
  // Reconstructed Ownership (BOI/PEP) results in the same shape.
  ownershipResults: any;
  entity?: { primary?: string; country?: string; address?: string };
  // When exporting to PDF, render the data-backed detail panels expanded so
  // their content is captured in the snapshot.
  pdfMode?: boolean;
}

// Strip wrapping quotes the LLM sometimes adds around the entity name in its
// rationale ("'Acme Corp' was flagged..."). Mirrors the Entity Screening tool.
const stripEntityNameQuotes = (
  text: string | undefined,
  entityName: string | undefined,
): string => {
  if (!text) return '';
  const name = (entityName || '').trim();
  if (!name) return text;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(
    new RegExp(`['"“‘’”\`]\\s*(${escaped})\\s*['"“‘’”\`]`, 'gi'),
    '$1',
  );
};

const EntityTicketResults: React.FC<IEntityTicketResultsProps> = ({
  aiSums = [],
  beneficialOwnership = null,
  adverseMedia = null,
  results,
  ownershipResults,
  entity,
  pdfMode = false,
}) => {
  // The Saved Work page is view-only: it never runs AI. Adverse Media and
  // Beneficial Ownership are driven entirely by what was saved with the ticket.
  const hasAdverseMedia = Boolean(
    adverseMedia && (adverseMedia.summary || adverseMedia.findings?.length),
  );
  const hasBeneficialOwnership = Boolean(
    beneficialOwnership &&
      (beneficialOwnership.summary ||
        beneficialOwnership.beneficial_ownership_summary ||
        beneficialOwnership.entity_risk ||
        beneficialOwnership.top_matches?.length),
  );

  const [page, setPage] = useState(0);
  const [entityInsightPanels, setEntityInsightPanels] = useState({
    ai: pdfMode,
    media: pdfMode && hasAdverseMedia,
    ownership: pdfMode && hasBeneficialOwnership,
  });

  // PDF export flips pdfMode on after mount, so expand the data-backed panels
  // then to ensure the saved detail lands in the snapshot.
  useEffect(() => {
    if (pdfMode) {
      setEntityInsightPanels((prev) => ({
        ...prev,
        ai: true,
        media: prev.media || hasAdverseMedia,
        ownership: prev.ownership || hasBeneficialOwnership,
      }));
    }
  }, [pdfMode]);

  const entityName = entity?.primary || '';
  const latestAiSummary = aiSums?.[0];
  const sanitizedExplanation = stripEntityNameQuotes(
    latestAiSummary?.explanation,
    latestAiSummary?.entity_name || entityName,
  );
  const latestAiTopMatches = Array.isArray(latestAiSummary?.top_matches)
    ? latestAiSummary.top_matches
        .map((match: any) =>
          typeof match === 'string' ? match : match?.name || match?.id,
        )
        .filter(Boolean)
    : [];
  const entityRiskLabel = latestAiSummary?.risk || 'N/A';
  const entityRiskClass = String(entityRiskLabel).toLowerCase().includes('low')
    ? 'low'
    : String(entityRiskLabel).toLowerCase().includes('high')
    ? 'high'
    : 'medium';
  const entityResultsTotal =
    results?.data?.[0]?.result?.body?.hits?.total?.value || 0;

  const toggleEntityInsightPanel = (key: 'ai' | 'media' | 'ownership') => {
    setEntityInsightPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div
      className='entity-screening-redesign'
      style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
    >
      <div className='entity-screening-insight-grid'>
        <div className='entity-screening-risk-card'>
          <div className='entity-screening-card-heading'>
            <ShieldOutlinedIcon sx={{ fontSize: 20 }} />
            <span>Overall Risk Evaluation</span>
          </div>
          <div className={`entity-screening-risk-value ${entityRiskClass}`}>
            {entityRiskLabel}
          </div>
          <p>
            {sanitizedExplanation ||
              'No risk evaluation was saved with this screen.'}
          </p>
          <small>
            Based on {entityResultsTotal.toLocaleString()} screening results
          </small>
        </div>

        <div
          className={`entity-screening-info-card${
            entityInsightPanels.ai ? ' active' : ''
          }`}
        >
          <div className='entity-screening-card-icon'>
            <AutoAwesomeOutlinedIcon sx={{ fontSize: 22 }} />
          </div>
          <div>
            <h3>AI Evaluation</h3>
            <p>Generated from matches</p>
          </div>
          <span>AI analysis of risk factors and contextual match insights</span>
          <button type='button' onClick={() => toggleEntityInsightPanel('ai')}>
            {entityInsightPanels.ai ? 'Hide Results' : 'View AI Evaluation'}
          </button>
        </div>

        <div
          className={`entity-screening-info-card${
            entityInsightPanels.media ? ' active' : ''
          }`}
          style={hasAdverseMedia ? undefined : { opacity: 0.6 }}
        >
          <div className='entity-screening-card-icon'>
            <ArticleOutlinedIcon sx={{ fontSize: 22 }} />
          </div>
          <div>
            <h3>Adverse Media</h3>
            <p>{hasAdverseMedia ? 'Saved with screen' : 'Not saved'}</p>
          </div>
          <span>
            {hasAdverseMedia
              ? 'Check for negative news and media coverage'
              : 'No adverse media results were saved with this screen.'}
          </span>
          {hasAdverseMedia ? (
            <button
              type='button'
              onClick={() => toggleEntityInsightPanel('media')}
            >
              {entityInsightPanels.media ? 'Hide Results' : 'View Results'}
            </button>
          ) : (
            <button type='button' disabled style={{ cursor: 'not-allowed' }}>
              Not saved
            </button>
          )}
        </div>

        <div
          className={`entity-screening-info-card${
            entityInsightPanels.ownership ? ' active' : ''
          }`}
          style={hasBeneficialOwnership ? undefined : { opacity: 0.6 }}
        >
          <div className='entity-screening-card-icon'>
            <GroupsOutlinedIcon sx={{ fontSize: 22 }} />
          </div>
          <div>
            <h3>Beneficial Ownership</h3>
            <p>{hasBeneficialOwnership ? 'Saved with screen' : 'Not saved'}</p>
          </div>
          <span>
            {hasBeneficialOwnership
              ? 'Identify ultimate beneficial owners when available'
              : 'No beneficial ownership results were saved with this screen.'}
          </span>
          {hasBeneficialOwnership ? (
            <button
              type='button'
              onClick={() => toggleEntityInsightPanel('ownership')}
            >
              {entityInsightPanels.ownership ? 'Hide Results' : 'View Results'}
            </button>
          ) : (
            <button type='button' disabled style={{ cursor: 'not-allowed' }}>
              Not saved
            </button>
          )}
        </div>
      </div>

      {(entityInsightPanels.ai ||
        (entityInsightPanels.media && hasAdverseMedia) ||
        (entityInsightPanels.ownership && hasBeneficialOwnership)) && (
        <div className='entity-screening-detail-stack'>
          {entityInsightPanels.ai && (
            <section className='entity-screening-detail-panel'>
              <button
                type='button'
                className='entity-screening-detail-close'
                onClick={() => toggleEntityInsightPanel('ai')}
              >
                x
              </button>
              <div className='entity-screening-detail-title'>
                <span className='entity-screening-card-icon'>
                  <AutoAwesomeOutlinedIcon sx={{ fontSize: 22 }} />
                </span>
                <h3>AI Evaluation</h3>
              </div>
              <div className='entity-screening-detail-body'>
                <p className='entity-screening-detail-summary'>
                  Based on {entityResultsTotal.toLocaleString()} screening
                  results, the entity profile shows{' '}
                  <strong className={entityRiskClass}>
                    {String(entityRiskLabel).toLowerCase()} risk exposure
                  </strong>
                  .{' '}
                  {sanitizedExplanation ||
                    'No risk narrative was saved with this screen.'}
                </p>
                <div className='entity-screening-top-matches entity-screening-top-matches-card'>
                  <h4 className='screening-ai-sum-header-text'>Top Matches</h4>
                  {latestAiTopMatches.length > 0 ? (
                    <div className='screening-ai-sum-result-text'>
                      <ol>
                        {latestAiTopMatches.slice(0, 5).map((match: string) => (
                          <li key={match}>
                            <strong>{match}</strong>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : (
                    <p>No top matches available for this evaluation.</p>
                  )}
                </div>
              </div>
              <small>Generated from the saved screening results</small>
            </section>
          )}

          {entityInsightPanels.media && hasAdverseMedia && (
            <section className='entity-screening-detail-panel'>
              <button
                type='button'
                className='entity-screening-detail-close'
                onClick={() => toggleEntityInsightPanel('media')}
              >
                x
              </button>
              <div className='entity-screening-detail-title'>
                <span className='entity-screening-card-icon'>
                  <ArticleOutlinedIcon sx={{ fontSize: 22 }} />
                </span>
                <h3>Adverse Media</h3>
              </div>
              <p className='entity-screening-detail-summary'>
                {adverseMedia?.summary ||
                  'No summary was saved for this adverse media result.'}
              </p>
              {adverseMedia?.findings?.length ? (
                <ul className='entity-screening-detail-list'>
                  {adverseMedia.findings
                    .slice(0, 5)
                    .map((finding: any, idx: number) => {
                      const safeUrl = normalizeExternalUrl(finding.url);

                      return (
                        <li key={`${finding.url || finding.claim}-${idx}`}>
                          {safeUrl ? (
                            <a
                              href={safeUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              {finding.claim || finding.source || 'Source'}
                            </a>
                          ) : (
                            finding.claim || finding.source || 'Finding'
                          )}
                          {finding.date ? ` - ${finding.date}` : ''}
                        </li>
                      );
                    })}
                </ul>
              ) : null}
            </section>
          )}

          {entityInsightPanels.ownership && hasBeneficialOwnership && (
            <section className='entity-screening-detail-panel'>
              <button
                type='button'
                className='entity-screening-detail-close'
                onClick={() => toggleEntityInsightPanel('ownership')}
              >
                x
              </button>
              <div className='entity-screening-detail-title'>
                <span className='entity-screening-card-icon'>
                  <GroupsOutlinedIcon sx={{ fontSize: 22 }} />
                </span>
                <h3>Beneficial Ownership</h3>
                {beneficialOwnership?.entity_risk && (
                  <div className='entity-screening-boi-risk'>
                    <span>Entity Risk</span>
                    <strong
                      className={String(
                        beneficialOwnership.entity_risk,
                      ).toLowerCase()}
                    >
                      {beneficialOwnership.entity_risk}
                    </strong>
                  </div>
                )}
              </div>
              <div className='entity-screening-detail-body'>
                <p className='entity-screening-detail-summary'>
                  {beneficialOwnership?.summary ||
                    beneficialOwnership?.beneficial_ownership_summary ||
                    'No summary was saved for this beneficial ownership result.'}
                </p>
                {beneficialOwnership?.top_matches?.length ? (
                  <div className='entity-screening-top-matches entity-screening-top-matches-card'>
                    <h4 className='screening-ai-sum-header-text'>
                      Top Matches
                    </h4>
                    <div className='screening-ai-sum-result-text'>
                      <ol>
                        {beneficialOwnership.top_matches
                          .slice(0, 5)
                          .map((match: string, idx: number) => (
                            <li key={`${match}-${idx}`}>{match}</li>
                          ))}
                      </ol>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          )}
        </div>
      )}

      <div className='entity-screening-results-heading'>
        <h2>Screening Results</h2>
        <p>
          Entity matches across selected lenses. Click any row to view match
          details.
        </p>
      </div>
      <div className='entity-screening-results-shell'>
        <EntityScreeningResultsTable
          isStatic
          handleSubmit={() => undefined}
          setPage={setPage}
          page={page}
          results={results}
          ownershipResults={ownershipResults}
          searchTerm={entityName}
        />
      </div>
    </div>
  );
};

export default EntityTicketResults;
